import { Component, Input, OnChanges, OnInit } from '@angular/core';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import * as Highcharts from 'highcharts';
import { HighchartsChartModule } from 'highcharts-angular';
import { Apps } from '../../interfaces/apps.interface';
import { NgClass, NgIf } from '@angular/common';

@Component({
  selector: 'wm-libs-workers-stats-chart',
  templateUrl: './workers-stats-chart.component.html',
  styleUrls: ['./workers-stats-chart.component.scss'],
  standalone: true,
  imports: [HighchartsChartModule, TranslateModule, NgClass, NgIf],
})
export class WorkersStatsChartComponent implements OnInit, OnChanges {
  Highcharts: typeof Highcharts = Highcharts;
  chartOptions!: Highcharts.Options;

  @Input() workers!: any; // Ideally, specify the type for workers if possible.
  @Input() workersInterface!: unknown;
  @Input() app!: Apps;

  workersStats: any;
  translationKeys: string[] = [];
  hasData = false;

  constructor(private translate: TranslateService) {
    this.initializeHighcharts();
  }

  ngOnInit(): void {
    this.loadChartData();
  }

  ngOnChanges(): void {
    this.loadChartData();

    if ((this.app === 'wm-tech-mms' && this.workers?.totalCount > 0) || (this.app === 'wm-tech-pool' && this.workers?.count > 0)) {
      this.hasData = true;
    } else {
      this.hasData = false;
    }
  }

  initializeHighcharts(): void {
    if (typeof Highcharts === 'object') {
      Highcharts.setOptions({
        time: {
          useUTC: false,
        },
        colors: ['#57D374', '#FF8264', '#EABD26', '#FF3838'],
      });
    }
  }

  loadChartData(): void {
    if (this.app === 'wm-tech-mms') {
      this.setupMmsChart();
    } else if (this.app === 'wm-tech-pool') {
      this.setupPoolChart();
    }

    this.translate.stream(this.translationKeys).subscribe((translations) => {
      this.chartOptions = this.buildChartOptions(translations);
    });
  }

  setupMmsChart(): void {
    this.translationKeys = ['WORKERS.STABLE', 'WORKERS.UNSTABLE', 'WORKERS.DOWN']; //'WORKERS.UP',

    const stableCount = this.workers?.summary?.stableCount || 0;
    const unstableCount = this.workers?.summary?.unstableCount || 0;
    const downCount = this.workers?.summary?.downCount || 0;
    // const upCount = stableCount + unstableCount;

    this.workersStats = [
      // { nameKey: 'WORKERS.UP', count: upCount, sliced: true },
      { nameKey: 'WORKERS.STABLE', count: stableCount },
      { nameKey: 'WORKERS.UNSTABLE', count: unstableCount },
      { nameKey: 'WORKERS.DOWN', count: downCount },
    ];
  }

  setupPoolChart(): void {
    this.translationKeys = ['WORKERS.UP', 'WORKERS.DOWN', 'WORKERS.UNSTABLE', 'WORKERS.DEAD'];

    const active = this.workers?.summary?.active || 0;
    const inactive = this.workers?.summary?.inactive || 0;
    const unstable = this.workers?.summary?.unstable || 0;
    const dead = this.workers?.summary?.dead || 0;

    this.workersStats = [
      { nameKey: 'WORKERS.UP', count: active, sliced: true },
      { nameKey: 'WORKERS.DOWN', count: inactive },
      { nameKey: 'WORKERS.UNSTABLE', count: unstable },
      { nameKey: 'WORKERS.DEAD', count: dead },
    ];
  }

  buildChartOptions(translations: any): Highcharts.Options {
    const seriesData = this.workersStats.map((stat: any) => ({
      name: `${stat.count} ${translations[stat.nameKey]}`,
      y: stat.count,
      sliced: stat.sliced || false,
      selected: stat.sliced || false,
    }));

    return {
      chart: {
        plotBackgroundColor: undefined,
        plotBorderWidth: undefined,
        plotShadow: false,
        type: 'pie',
      },
      title: {
        text: '',
      },
      tooltip: {
        pointFormat: '{series.name}: <b>{point.percentage:.1f}%</b>',
      },
      accessibility: {
        point: {
          valueSuffix: '%',
        },
      },
      plotOptions: {
        pie: {
          allowPointSelect: false,
          cursor: 'pointer',
          dataLabels: {
            enabled: false,
          },
          showInLegend: true,
        },
      },
      legend: {
        align: 'right',
        verticalAlign: 'middle',
        layout: 'vertical',
      },
      series: [
        {
          type: 'pie',
          name: translations['STATISTICS.WORKER'],
          data: seriesData,
        },
      ],
    };
  }
}
